import moment from 'moment/moment';
import DossierClient from '@/dossier/client/DossierClient';
import DossierContext from '@/dossier/models/DossierContext';
import { CheckInSettings } from '@/dossier/models/CheckInDetails';
import CheckInContext from '@/dossier/models/CheckInContext';
import VehicleStatus, { VEHICLE_STATUS_TRANSITIONS } from '@/dossier/constants/VehicleStatus';
import Location from '@/vehicle/models/Location';
import AuthService from '@/general/services/AuthService';
import Dossier from '@/dossier/models/Dossier';
import JobClient from '@/jobs/client/JobClient';
import { parseSignature } from '@/jobs/converters/jobConverters';
import { isPresent, present } from '@/shared/utils/typescript';
import { mergeInto, mergeLists } from '@/shared/utils';
import Job from '@/jobs/models/Job';
import DiagnosticService from '@/jobs/services/DiagnosticService';
import { ComplaintDiagnostic } from '@/jobs/models/diagnosis/Diagnostic';
import JobContext from '@/jobs/models/JobContext';

export default class CheckInService {
  static async getDetails(dossierContext: DossierContext): Promise<CheckInSettings> {
    const checkInDetails = await DossierClient.getCheckInDetails(dossierContext.dossier.id);

    // Update local data with latest data form checkin api call
    Object.assign(dossierContext.dossier, checkInDetails.dossier);
    Object.assign(dossierContext.dossierVehicle.vehicle, checkInDetails.vehicle);

    dossierContext.contactContext.contacts.forEach(contact => {
      const customer = contact.customer;
      const contactDetails = checkInDetails.contacts.find(c => c.id === customer.id);
      if (contactDetails) {
        Object.assign(customer, contactDetails);
      }
    });

    dossierContext.jobContext.jobs.forEach(job => {
      const jobDetails = checkInDetails.jobs.find(j => j.id === job.id);
      if (jobDetails) {
        Object.assign(job, jobDetails.details);
        Object.assign(job.price, jobDetails.price);
        Object.assign(job.order, jobDetails.order);
        mergeLists(jobDetails.diagnostics, job.diagnostics);
      }
    });

    return checkInDetails.settings;
  }

  static async save(context: CheckInContext): Promise<void> {
    const dossier = context.dossierContext.dossier;
    const vehicle = context.dossierContext.dossierVehicle.vehicle;
    const data = context.formData;

    this.ensureValidVehicleStatusTransition(dossier, VehicleStatus.CHECKED_IN);

    const changedJobs = context.editableJobContext.jobs.filter(j => j.hasApprovalChanges());

    if (data.contact && (changedJobs.length > 0 || data.selectedUpsellItemIds.length > 0)) {
      await JobClient.setJobStatus(data, changedJobs, dossier.id, true);
      changedJobs.forEach(j => {
        j.order.signature = parseSignature(data.signature);
        j.order.hasSignature = isPresent(data.signature);
        j.original?.updateWith(j);
      });
    }

    await DossierClient.markVehicleCheckedIn2(data, dossier);
    dossier.vehicleStatus = VehicleStatus.CHECKED_IN;
    dossier.hasFluidReplenishmentApproval = data.refillLiquids;
    dossier.hasConsumptionDataApproval = data.hasConsumptionDataApproval;
    const location = vehicle.location?.isEqualTo(data.position, data.locationDescription)
      ? vehicle.location
      : Location.create(data.locationDescription, data.position, moment(), AuthService.getCurrentUser());

    vehicle.update(data, location);
  }

  private static ensureValidVehicleStatusTransition(dossier: Dossier, to: VehicleStatus): void {
    const from = dossier.vehicleStatus;
    if (from === null || !VEHICLE_STATUS_TRANSITIONS[from].includes(to)) throw new Error(`Invalid vehicle status transition from ${from} to ${to}`);
  }

  static async saveJobEdit(context: JobContext, data: { job: Job, diagnostic: ComplaintDiagnostic }): Promise<void> {
    await JobClient.updatePrice(context.dossier.id, data.job);
    const job = present(context.getJobById(data.job.id));

    await DiagnosticService.save(
      {
        complaint: data.diagnostic.complaint,
        conditions: data.diagnostic.conditions,
      },
      data.diagnostic,
      data.job,
      context,
    );

    mergeInto(data.diagnostic, job.diagnostics);
  }
}